// hooks/useVideoTranslation.ts
import { useState, useRef, useCallback, useEffect } from 'react';
import {
  EnglishCaptionJob,
  TranslationJob,
  UseVideoTranslationReturn,
  VideoTranslationOptions,
} from '@types';
import { useCreateTranslationJobMutation, useGenerateEnglishCaptionsMutation, useGetEnglishCaptionJobLazyQuery, useGetTranslationJobLazyQuery } from '@src/api/graphql-codegen';

export const useVideoTranslation = (options: VideoTranslationOptions = {}): UseVideoTranslationReturn => {
  const {
    pollingInterval = 60000,
    onEnglishCaptionsComplete,
    onTranslationComplete,
    onError,
  } = options;

  const [englishCaptionJob, setEnglishCaptionJob] = useState<EnglishCaptionJob | null>(null);
  const [translationJob, setTranslationJob] = useState<TranslationJob | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const pollingRef = useRef<{
    englishCaption: NodeJS.Timeout | null;
    translation: NodeJS.Timeout | null;
  }>({
    englishCaption: null,
    translation: null,
  });

  const isMounted = useRef(true);

  // Mutations
  const [generateEnglishCaptionsMutation] = useGenerateEnglishCaptionsMutation();
  const [createTranslationJobMutation] = useCreateTranslationJobMutation();

  // Queries
  const [getEnglishCaptionJob] = useGetEnglishCaptionJobLazyQuery({fetchPolicy: 'network-only'});
  const [getTranslationJob] = useGetTranslationJobLazyQuery({fetchPolicy: 'network-only'});

  // Stop all polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current.englishCaption) {
      clearInterval(pollingRef.current.englishCaption);
      pollingRef.current.englishCaption = null;
    }
    if (pollingRef.current.translation) {
      clearInterval(pollingRef.current.translation);
      pollingRef.current.translation = null;
    }
  }, []);

  // Set mounted flag and cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  // Reset state
  const reset = useCallback(() => {
    stopPolling();
    setEnglishCaptionJob(null);
    setTranslationJob(null);
    setLoading(false);
    setError(null);
  }, [stopPolling]);

  const stopTranslationJob = useCallback(() => {
    stopPolling();
    setTranslationJob(null);
    setLoading(false);
    setError(null);
  }, [stopPolling]);

  // Poll English caption job
  const pollEnglishCaptionJob = useCallback(async (jobId: string) => {
    if (pollingRef.current.englishCaption) {
      clearInterval(pollingRef.current.englishCaption);
    }

    pollingRef.current.englishCaption = setInterval(async () => {
      try {
        const { data } = await getEnglishCaptionJob({
          variables: { id: jobId }
        });

        const job = data?.englishCaptionJob as EnglishCaptionJob;
        setEnglishCaptionJob(job);

        if (job.status === 'completed') {
          stopPolling();
          setLoading(false);
          onEnglishCaptionsComplete?.(job);
        } else if (job.status === 'failed') {
          stopPolling();
          setLoading(false);
          const errorMsg = job.errorMessage || 'English caption generation failed';
          setError(errorMsg);
          onError?.(errorMsg);
        }
      } catch (err) {
        stopPolling();
        setLoading(false);
        const errorMsg = err instanceof Error ? err.message : 'Failed to poll English caption job';
        setError(errorMsg);
        onError?.(errorMsg);
      }
    }, pollingInterval);
  }, [pollingInterval, getEnglishCaptionJob, stopPolling, onEnglishCaptionsComplete, onError]);

  // Poll translation job
  const pollTranslationJob = useCallback(async (jobId: string) => {
    if (pollingRef.current.translation) {
      clearInterval(pollingRef.current.translation);
    }

    pollingRef.current.translation = setInterval(async () => {
      try {
        const { data } = await getTranslationJob({
          variables: { id: jobId }
        });

        const job = data?.translationJob as TranslationJob;
        setTranslationJob(job);

        if (job.status === 'completed' || job.status === 'partial') {
          stopPolling();
          setLoading(false);
          onTranslationComplete?.(job);
        } else if (job.status === 'failed') {
          stopPolling();
          setLoading(false);
          const errorMsg = job.errorMessage || 'Translation job failed';
          setError(errorMsg);
          onError?.(errorMsg);
        }
      } catch (err) {
        stopPolling();
        setLoading(false);
        const errorMsg = err instanceof Error ? err.message : 'Failed to poll translation job';
        setError(errorMsg);
        onError?.(errorMsg);
      }
    }, pollingInterval);
  }, [pollingInterval, getTranslationJob, stopPolling, onTranslationComplete, onError]);

  // Generate English captions
  const generateEnglishCaptions = useCallback(async (assetId: string) => {
    try {
      reset();
      setLoading(true);
      setError(null);

      const { data } = await generateEnglishCaptionsMutation({
        variables: {
          input: { assetId },
        },
      });

      const job = data?.generateEnglishCaptions as EnglishCaptionJob;
      setEnglishCaptionJob(job);

      // Start polling for progress
      await pollEnglishCaptionJob(job.id);
    } catch (err) {
      setLoading(false);
      const errorMsg = err instanceof Error ? err.message : 'Failed to generate English captions';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [generateEnglishCaptionsMutation, pollEnglishCaptionJob, reset, onError]);

  // Create translation job
  const createTranslationJob = useCallback(async (assetId: string, targetLanguages?: string[]) => {
    try {
      // reset();
      setLoading(true);
      setError(null);

      const { data } = await createTranslationJobMutation({
        variables: {
          input: {
            assetId,
            targetLanguages: targetLanguages || [],
          },
        },
      });

      const job = data?.createTranslationJob as TranslationJob;
      setTranslationJob(job);

      // Start polling for progress
      await pollTranslationJob(job.id);
    } catch (err) {
      setLoading(false);
      const errorMsg = err instanceof Error ? err.message : 'Failed to create translation job';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [createTranslationJobMutation, pollTranslationJob, onError]);

  const downloadFile = useCallback(async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const errorMsg = `Failed to download file: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [onError]);

  const downloadEnglishSubtitles = useCallback(() => {
    if (!englishCaptionJob?.subtitleUrl) return;
    
    const url = englishCaptionJob.subtitleUrl;
    const filename = `english-subtitles-${englishCaptionJob.id}.${getFileExtension(url)}`;
    downloadFile(url, filename);
  }, [englishCaptionJob, downloadFile]);

  const downloadTranslationSubtitles = useCallback((languageCode: string) => {
    if (!translationJob?.languageJobs) return;
    
    const languageJob = translationJob.languageJobs.find(job => job.languageCode === languageCode);
    if (!languageJob?.subtitleUrl) return;
    
    const url = languageJob.subtitleUrl;
    const filename = `${languageCode}-subtitles-${translationJob.id}.${getFileExtension(url)}`;
    downloadFile(url, filename);
  }, [translationJob, downloadFile]);

  // Helper function to get file extension from URL
  const getFileExtension = (url: string): string => {
    const extension = url.split('.').pop()?.toLowerCase();
    return extension || 'srt'; // default to SRT if cannot determine
  };

  // Status helpers
  const isProcessing = loading || 
    englishCaptionJob?.status === 'processing' || 
    translationJob?.status === 'processing' ||
    translationJob?.status === 'generating_english';

  const isCompleted = 
    englishCaptionJob?.status === 'completed' || 
    translationJob?.status === 'completed' ||
    translationJob?.status === 'partial';

  const hasError = 
    englishCaptionJob?.status === 'failed' || 
    translationJob?.status === 'failed' || 
    !!error;

  const overallProgress = translationJob?.progressPercentage || englishCaptionJob?.progressPercentage || 0;

  // Cleanup on unmount
  useState(() => {
    return () => {
      stopPolling();
    };
  });

  return {
    // States
    englishCaptionJob,
    translationJob,
    loading,
    error,
    
    // Actions
    generateEnglishCaptions,
    createTranslationJob,
    stopPolling,
    reset,
    stopTranslationJob,
    downloadFile,
    downloadEnglishSubtitles,
    downloadTranslationSubtitles,
    
    // Status helpers
    isProcessing,
    isCompleted,
    hasError,
    overallProgress,
  };
};